import React, { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PageContainer from './PageContainer';
import { LayoutShellProvider } from '../../contexts/LayoutShellContext';
import { GenericContentSkeleton } from './RoutePermissionGuard';

/**
 * AuthenticatedShell Component
 * 
 * DESIGN RATIONALE:
 * - Serves as the persistent Root Layout Shell for all authenticated routes.
 * - This component stays mounted during navigation between nested routes.
 * - Because Sidebar and Header are inside PageContainer here, they NEVER unmount.
 * - Individual pages inside the Outlet can still call PageContainer (polymorphic), 
 *   but those calls will just sync the title to the root shell instead of re-rendering layout.
 * - This completely eliminates the "Blinking" effect during navigation.
 * 
 * CROSS-DISSOLVE ANIMATION:
 * - The `animate-page-entry` wrapper uses `key={location.pathname}` to force
 *   React to unmount/remount the animation container on every route change.
 * - This re-triggers the CSS `pageEntry` keyframe, creating a smooth fade-in
 *   from skeleton → page content using GPU-accelerated opacity + translateY.
 * - The animation is defined in globals.css and respects `prefers-reduced-motion`.
 */
const AuthenticatedShell: React.FC = () => {
    const location = useLocation();

    return (
        <LayoutShellProvider inShell={false}>
            <PageContainer pageTitle="">
                {/* Nested Suspense for page fragments ensures Layout stays visible while page loads */}
                <Suspense fallback={<GenericContentSkeleton />}>
                    {/* 
                      Keyed animation wrapper: forces remount on route change,
                      re-triggering the cross-dissolve CSS animation.
                      The key uses pathname only (not search/hash) to prevent
                      unnecessary re-animations on query param changes.
                    */}
                    <div key={location.pathname} className="animate-page-entry">
                        <Outlet />
                    </div>
                </Suspense>
            </PageContainer>
        </LayoutShellProvider>
    );
};

export default AuthenticatedShell;
